<script setup lang="ts">
interface Props {
  userType?: 'all' | 'guest' | 'owner'
}
const props = withDefaults(defineProps<Props>(), {
  userType: 'all',
})

interface MenuItem {
  id: 'guest' | 'owner'
  title: string
  steps: {
    thematic: string
    title: string
    description: string
    icon: ReturnType<typeof resolveComponent>
  }[]
}

const { t } = useI18n()

const activeItem = ref<MenuItem['id']>(props.userType === 'all' ? 'owner' : props.userType)

const isOwner = computed(() => props.userType === 'owner')
const isGuest = computed(() => props.userType === 'guest')

const content = computed<Record<MenuItem['id'], MenuItem>>(() => ({
  owner: {
    id: 'owner',
    title: t('components.app_how_it_works.nav.owner.title'),
    steps: [
      {
        thematic: t('components.app_how_it_works.owner.steps.register.thematic'),
        title: t('components.app_how_it_works.owner.steps.register.title'),
        description: t('components.app_how_it_works.owner.steps.register.description'),
        icon: resolveComponent('YscIconsVehicle'),
      },
      {
        thematic: t('components.app_how_it_works.owner.steps.requests.thematic'),
        title: t('components.app_how_it_works.owner.steps.requests.title'),
        description: t('components.app_how_it_works.owner.steps.requests.description'),
        icon: resolveComponent('YscIconsCalendarCheck'),
      },
      {
        thematic: t('commons.links.contract'),
        title: t('components.app_how_it_works.owner.steps.contract.title'),
        description: t('components.app_how_it_works.owner.steps.contract.description'),
        icon: resolveComponent('YscIconsThumbUp'),
      },
      {
        thematic: t('components.app_how_it_works.owner.steps.start.thematic'),
        title: t('components.app_how_it_works.owner.steps.start.title'),
        description: t('components.app_how_it_works.owner.steps.start.description'),
        icon: resolveComponent('YscIconsMoneyBox'),
      },
    ],
  },
  guest: {
    id: 'guest',
    title: t('components.app_how_it_works.nav.guest.title'),
    steps: [
      {
        thematic: t('components.app_how_it_works.guest.steps.search.thematic'),
        title: t('components.app_how_it_works.guest.steps.search.title'),
        description: t('components.app_how_it_works.guest.steps.search.description'),
        icon: resolveComponent('YscIconsVehicle'),
      },
      {
        thematic: t('components.app_how_it_works.guest.steps.confirme.thematic'),
        title: t('components.app_how_it_works.guest.steps.confirme.title'),
        description: t('components.app_how_it_works.guest.steps.confirme.description'),
        icon: resolveComponent('YscIconsCalendarCheck'),
      },
      {
        thematic: t('components.app_how_it_works.guest.steps.travel.thematic'),
        title: t('components.app_how_it_works.guest.steps.travel.title'),
        description: t('components.app_how_it_works.guest.steps.travel.description'),
        icon: resolveComponent('YscIconsWorld'),
      },
      {
        thematic: t('commons.trip_back'),
        title: t('components.app_how_it_works.guest.steps.back.title'),
        description: t('components.app_how_it_works.guest.steps.back.description'),
        icon: resolveComponent('YscIconsThumbUp'),
      },
    ],
  },
}))

const isActive = (menuItem: string) => activeItem.value === menuItem
const setActive = (menuItem: MenuItem['id']) => (activeItem.value = menuItem)
</script>

<template>
  <div class="bg-gray-50 py-12 lg:py-20">
    <div class="container">
      <div class="mx-auto mb-12 max-w-prose space-y-4 lg:mb-16">
        <YscHeading
          v-if="$slots.title"
          level="3"
          class="text-center text-2xl font-semibold leading-tight md:text-4xl"
        >
          <slot name="title">
            {{ $t('commons.how_it_works') }}
          </slot>
        </YscHeading>

        <YscHeading
          v-if="$slots.subtitle"
          level="4"
          class="text-center opacity-70 md:text-xl"
        >
          <slot name="subtitle" />
        </YscHeading>
      </div>

      <ul
        v-if="!isOwner && !isGuest"
        class="mb-16 flex justify-center gap-x-6 md:gap-x-0 md:divide-x-2 md:divide-gray-300"
      >
        <li
          v-for="nav in content"
          :key="`$HIW_nav_${nav.id}`"
          class="flex-1 first:text-right md:px-12"
        >
          <button
            type="button"
            :class="isActive(nav.id) ? 'border-peacock-500 border-b-2 font-semibold' : 'font-light'"
            class="hover:border-peacock-500 relative inline box-decoration-clone py-1 hover:border-b-2 focus-visible:ring-2 focus-visible:ring-blue-500 md:text-2xl"
            @click="setActive(nav.id)"
          >
            {{ nav.title }}
          </button>
        </li>
      </ul>

      <transition
        name="fade"
        mode="out-in"
      >
        <AppHowItWorksItem
          :key="activeItem"
          :steps="content[activeItem].steps"
          :user-type="content[activeItem].id"
        />
      </transition>
    </div>
  </div>
</template>
